import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ClickIcon from "../../icon-buttons/click-icon/ClickIcon";

import "./AcedModal.scss";

export interface AcedModalProps {
	title?: string;
	state: {
		isModalOpen: boolean;
		setModalOpen: (open: boolean) => void;
	};
	children: React.ReactNode;
	onClose?: any;
	attachRef?: React.RefObject<HTMLElement>;
	hideClose?: boolean;
	bgColor?: string;

	className?: string;
	style?: any;
}

const AcedModal = ({
	title,
	state,
	children,
	onClose,
	attachRef,
	hideClose,
	bgColor,
	className,
	style,
}: AcedModalProps) => {
	const overlayRef = useRef<HTMLDivElement | null>(null);
	const modalRef = useRef<HTMLDivElement | null>(null);
	const [modalStyle, setModalStyle] = useState<React.CSSProperties>({});

	// Handle ESC key and scroll lock
	useLayoutEffect(() => {
		if (!state.isModalOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				state.setModalOpen(false);
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		document.body.style.overflow = "hidden";

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "";
		};
	}, [state.isModalOpen]);

	// Positioning logic
	useLayoutEffect(() => {
		if (!state.isModalOpen) return;

		const updatePosition = () => {
			const modal = modalRef.current;
			if (!modal) return;

			if (attachRef?.current) {
				const attachRect = attachRef.current.getBoundingClientRect();
				const modalRect = modal.getBoundingClientRect();
				const padding = 8;

				let top = attachRect.bottom + padding;
				let left = attachRect.left;

				// Adjust if overflowing the right
				if (left + modalRect.width > window.innerWidth) {
					left = window.innerWidth - modalRect.width - padding;
				}

				// Adjust if overflowing the bottom
				if (top + modalRect.height > window.innerHeight) {
					top = attachRect.top - modalRect.height - padding;
				}

				// Clamp to screen
				if (top < padding) top = padding;
				if (left < padding) left = padding;

				setModalStyle({
					position: "absolute",
					top: `${top + window.scrollY}px`,
					left: `${left + window.scrollX}px`,
				});
			} else {
				// Fallback to centered layout
				setModalStyle({});
			}
		};

		updatePosition();

		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition);

		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition);
		};
	}, [state.isModalOpen, attachRef]);

	const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === overlayRef.current) {
			state.setModalOpen(false);
		}
	};

	if (!state.isModalOpen) return null;

	const Style = {
		"--bg-color": bgColor ?? "#151b2c",
	} as React.CSSProperties;

	return createPortal(
		<div
			style={Style}
			ref={overlayRef}
			className={`modal-overlay ${className || ""}`}
			onClick={handleClickOutside}
		>
			<div
				ref={modalRef}
				className="modal-container"
				role="dialog"
				aria-modal="true"
				style={{ ...modalStyle, ...style }}
			>
				{(!hideClose || title) && (
					<div className="modal-header">
						{title && <p className="mtitle">{title}</p>}
						{!hideClose && (
							<ClickIcon
								icon="close"
								onClick={onClose ? onClose : () => state.setModalOpen(false)}
							/>
						)}
					</div>
				)}
				<div className="modal-body">{children}</div>
			</div>
		</div>,
		document.getElementById("root")!
	);
};

export default AcedModal;
